import { useMemo, useRef, useState } from 'react';
import { MultiValue, SelectInstance, StylesConfig } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';

interface Option {
  value: string;
  label: string;
}

interface Props {
  className?: string;
  value: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onDropdownExpand?: (searchValue: string) => string[] | Promise<string[]>;
}

export const InputTextSelect = ({
  className,
  value,
  placeholder,
  onChange,
  onDropdownExpand,
}: Props) => {
  const asyncRef = useRef<SelectInstance<Option> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const promiseOptions = async (val: string) => {
    if (!onDropdownExpand) return [];

    setIsLoading(true);
    const result = await onDropdownExpand(val);
    setIsLoading(false);

    return result.map(createOption);
  };

  const variableValue: Option[] = useMemo(() => commaTextToOptions(value), [value]);

  const handleBlur = () => {
    if (!inputValue) return;

    //user can enter a few values separated by commas
    const newItems = commaTextToOptions(inputValue);
    const commaText = [...variableValue, ...newItems].map((i) => i.label).join(',');

    onChange?.(commaText);
    setInputValue('');
  };

  const handleChange = (newValue: MultiValue<Option>) => {
    const result = newValue.map((i) => i.label).join(',');
    onChange?.(result);
  };

  return (
    <AsyncCreatableSelect
      ref={asyncRef}
      className={className}
      classNamePrefix="editable-search"
      styles={componentStyles as any}
      //cacheOptions
      defaultOptions
      isMulti
      isClearable
      allowCreateWhileLoading
      isLoading={isLoading}
      inputValue={inputValue}
      value={variableValue}
      placeholder={placeholder}
      createOptionPosition="first"
      components={{ DropdownIndicator: null }}
      noOptionsMessage={() => null}
      formatCreateLabel={(inputValue) => inputValue}
      loadOptions={promiseOptions}
      onBlur={handleBlur}
      onInputChange={setInputValue}
      onChange={(r) => handleChange(r as MultiValue<Option>)}
    />
  );
};

const componentStyles: StylesConfig<Option> = {
  container: (base, state) => ({
    ...base,
    backgroundColor: 'white',
    border: '1px solid',
    borderColor: state.isFocused
      ? 'var(--ap-color-ui-green-primary)'
      : 'var(--ap-color-grey3)',
  }),
  control: (base) => ({
    ...base,
    minHeight: '28px',
    borderStyle: 'none',
    borderColor: 'unset',
    borderRadius: 'unset',
    backgroundColor: 'unset',
    boxShadow: 'unset',
  }),
  clearIndicator: (base) => ({ ...base, padding: '0 8px' }),
  placeholder: (base) => ({ ...base, color: 'var(--ap-color-grey4)' }),
  menu: (base) => ({ ...base, zIndex: 11 }),
};

const commaTextToOptions = (text: string) => {
  const trimmedText = text.trim();
  if (!trimmedText) return [];

  return trimmedText
    .split(',')
    .map((r) => r.trim())
    .filter((r) => r)
    .map(createOption);
};

const createOption = (label: string, id: number): Option => ({
  label,
  value: `${id} ${label}`,
});
